import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import './Login.css';
import { getApiUrl } from '../config/api';

const Login = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [message, setMessage] = useState('');
  const [view, setView] = useState('login'); // 'login' | 'reset' | 'resetSent'
  const [resetEmail, setResetEmail] = useState('');
  const [resetMessage, setResetMessage] = useState('');
  const [resetLoading, setResetLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    
    try {
      const response = await fetch(getApiUrl('/api/v1/auth/login'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        const data = await response.json();
        
        // Store tokens in localStorage
        localStorage.setItem('access_token', data.access_token);
        localStorage.setItem('refresh_token', data.refresh_token);
        localStorage.setItem('user_id', data.user_id);
        localStorage.setItem('first_name', data.first_name);
        
        // Redirect to dashboard
        navigate('/dashboard');
      } else {
        const errorData = await response.json();
        setMessage(errorData.error || 'Login failed. Please try again.');
      }
    } catch (error) {
      console.error('Error:', error);
      setMessage('An error occurred during login.');
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleLogin(e);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!resetEmail.trim()) return;

    setResetLoading(true);
    setResetMessage('');

    try {
      const response = await fetch(getApiUrl('/api/v1/auth/password/forgot'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: resetEmail.trim() }),
      });

      if (response.ok) {
        setView('resetSent');
      } else {
        const errorData = await response.json();
        setResetMessage(errorData.error || 'Something went wrong. Please try again.');
      }
    } catch (error) {
      console.error('Error:', error);
      setResetMessage('An error occurred. Please try again.');
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <div className="page-container">
      <div className="login-wrapper">
        <h1 className="text-4xl font-bold text-green-700">ZenRent</h1>
        <div className="login-box">

          {/* Login View */}
          {view === 'login' && (
            <>
              <h2 className="text-xl font-semibold text-gray-800">Log in to your account</h2>
              <p className="text-gray-600">Enter your credentials below</p>

              <form onSubmit={handleLogin}>
                <input
                  type="email"
                  name="email"
                  placeholder="Email"
                  className="mt-3 w-full p-2 border rounded focus:ring-2 focus:ring-green-400"
                  value={formData.email}
                  onChange={handleChange}
                />

                <input
                  type="password"
                  name="password"
                  placeholder="Password"
                  className="mt-3 w-full p-2 border rounded focus:ring-2 focus:ring-green-400"
                  value={formData.password}
                  onChange={handleChange}
                  onKeyDown={handleKeyDown}
                />

                <button
                  type="submit"
                  className="mt-4 w-full p-2 bg-green-700 text-white rounded hover:bg-green-800"
                >
                  Log in
                </button>
              </form>

              <div className="mt-4 text-center">
                <p>Don't have an account? <span className="text-green-700 cursor-pointer" onClick={() => navigate('/signup')}>Sign up</span></p>
              </div>

              <div className="mt-3">
                <a
                  href="#"
                  className="text-sm text-green-700 no-underline cursor-pointer hover:underline"
                  onClick={(e) => {
                    e.preventDefault();
                    setResetEmail('');
                    setResetMessage('');
                    setView('reset');
                  }}
                >
                  Forgot your password?
                </a>
              </div>
            </>
          )}

          {/* Reset Password View */}
          {view === 'reset' && (
            <>
              <h2 className="text-xl font-semibold text-gray-800">Reset your password</h2>
              <p className="text-gray-600 text-sm leading-normal">
                Enter the email address associated with your account and we'll
                send you a link to reset your password.
              </p>

              <form onSubmit={handleForgotPassword}>
                <label className="block text-sm font-medium text-gray-700 mt-5 mb-1.5 text-left">Email address</label>
                <input
                  type="email"
                  placeholder="you@example.com"
                  className="w-full p-2 border rounded focus:ring-2 focus:ring-green-400"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  required
                />
                <button
                  type="submit"
                  disabled={resetLoading}
                  className="mt-5 w-full p-2 bg-green-700 text-white rounded cursor-pointer hover:bg-green-800 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {resetLoading ? 'Sending...' : 'Send reset link'}
                </button>
              </form>

              {resetMessage && (
                <p className="error-message mt-3">{resetMessage}</p>
              )}

              <button
                className="mt-3 w-full p-2 bg-transparent text-gray-600 border-0 rounded cursor-pointer hover:text-gray-800"
                onClick={() => setView('login')}
              >
                Cancel
              </button>
            </>
          )}

          {/* Reset Sent Confirmation View */}
          {view === 'resetSent' && (
            <div className="text-center">
              <div className="reset-success-icon bg-green-100 text-green-700">✓</div>
              <h2 className="text-xl font-semibold text-gray-800">Check your email</h2>
              <p className="text-gray-600 text-sm leading-normal">
                If you have a ZenRent account, you will receive a password
                reset email shortly.
              </p>
              <button
                className="mt-5 w-full p-2 bg-green-700 text-white rounded cursor-pointer hover:bg-green-800"
                onClick={() => {
                  setResetEmail('');
                  setView('login');
                }}
              >
                Back to login
              </button>
            </div>
          )}

        </div>

        {view === 'login' && message && (
          <p className={message.includes('successfully') ? 'success-message mt-4' : 'error-message mt-4'}>
            {message}
          </p>
        )}
      </div>
    </div>
  );
};

export default Login;
